/**
 * Open-redirect guard for the `?next=` parameter.
 *
 * The post-auth redirect target is attacker-controllable via the URL
 * (`?next=https://evil…`, `?next=//evil…`, `?next=/\evil`, etc.). We
 * accept ONLY a same-origin absolute path: a single leading slash that
 * is not the start of a scheme or a protocol-relative URL. Anything
 * else collapses to the safe default.
 *
 * Allowed:    /w/acme/hr, /w/acme/leave/requests?tab=pending, /a/b#c
 * Rejected:   //evil.com, https://evil.com, /\evil.com, "", null,
 *             /path with spaces, javascript:alert(1)
 *
 * Slug-prefix URL refactor (2026-06-03): callers that need a workspace
 * hub on the fallback path (login, register) translate the bare "/"
 * fallback into `/w/{user.tenant.slug}` after /me resolves. The safe-
 * next helper itself stays workspace-agnostic — its only job is
 * validating that a path is same-origin.
 */
export const DEFAULT_NEXT = "/";

// Leading single "/" that is NOT followed by another "/" (protocol-
// relative) and contains no whitespace. A backslash after the slash
// (e.g. "/\evil.com") is non-whitespace but is normalized by browsers
// to "//evil.com", so reject any backslash too.
const SAFE_NEXT_RE = /^\/(?!\/)[^\s\\]*$/;

export function safeNext(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_NEXT;
  return SAFE_NEXT_RE.test(raw) ? raw : DEFAULT_NEXT;
}

/**
 * Workspace hub URL for a given tenant slug. Auth-flow callers
 * (login, register, /select-workspace, /2fa-verify) use this when
 * `safeNext` returned the bare "/" fallback — they need to land the
 * authenticated user on their workspace, not the marketing root.
 *
 * Returns "/" when slug is undefined so callers can chain it safely
 * without a separate null check.
 */
export function workspaceHubFor(slug: string | undefined | null): string {
  return slug ? `/app/${slug}` : "/";
}

/**
 * Resolve the post-auth landing path: honour an explicit safe ?next=
 * when present, otherwise send the user to their workspace hub. The
 * `next` arg must already be safeNext-validated by the caller.
 */
export function postAuthRedirect(
  next: string,
  slug: string | undefined | null,
): string {
  return next === DEFAULT_NEXT ? workspaceHubFor(slug) : next;
}
